import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import MainCard from "../../../../../../components/MainCard";

import { useTheme } from "@mui/material/styles";
import {
  SnackbarProps,
  Stack,
  Tooltip,
  Typography,
  IconButton,
  Button,
} from "@mui/material";
import _debounce from "lodash/debounce";

import { ColumnDef } from "@tanstack/react-table";

import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import Loader from "../../../../../../components/Loader";
import { FormattedMessage } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import ReusableSimpleTable from "../../../../../../components/ReusableComponent/ReusableSimpleTable";
import { openSnackbar } from "../../../../../../api/snackbar";
import ReusableDeleteDialog from "../../../../../../components/ReusableComponent/ReusableDeleteDialog";
import { EyeOutlined, CloseOutlined } from "@ant-design/icons";
import ExpandingResumeCourseDetail from "./ExpandingResumeLanguageDetail";
import { ResumeLanguage } from "../../../../../../types/resumes-managment/resume";
import AddEditResumeLanguageForm from "./AddEditResumeLanguageForm";

const ResumeLanguages = ({ resumeId }: { resumeId?: number }) => {
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);

  const [customerModal, setCustomerModal] = useState<boolean>(false);
  const [expandedRowId, setExpandedRowId] = useState<string | number | null>(
    null
  );
  const [selected, setSelected] = useState<ResumeLanguage | null>(null);

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResumeLanguage[]>([]);

  const [isDeleting, setIsDeleting] = useState(false);

  const handleClose = () => {
    setOpen(!open);
  };

  const columns = useMemo<ColumnDef<ResumeLanguage>[]>(
    () => [
      // {
      //   header: () => <FormattedMessage id="ID" />,
      //   accessorKey: "id",
      //   meta: { className: "cell-center" },
      //   cell: ({ row }) => (
      //     <Stack direction="row" spacing={1.5} alignItems="center">
      //       <Stack spacing={0}>
      //         <Typography variant="subtitle2">{row?.original.id}</Typography>
      //       </Stack>
      //     </Stack>
      //   ),
      // },

      {
        header: () => <FormattedMessage id="language id" />,
        accessorKey: "language_id",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.language_id}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Reading Level" />,
        accessorKey: "reading_level",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.reading_level}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Conversation Level" />,
        accessorKey: "conversation_level",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.conversation_level}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Writing Level" />,
        accessorKey: "writing_level",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.writing_level}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Created At" />, // Add created_at column
        accessorKey: "created_at",
        meta: { className: "cell-center" },
        cell: ({ row }) => (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Stack spacing={0}>
              <Typography variant="subtitle2">
                {row?.original.created_at}
              </Typography>
            </Stack>
          </Stack>
        ),
      },
      {
        header: () => <FormattedMessage id="Actions" />,
        accessorKey: "actions",
        meta: {
          className: "cell-center",
        },
        cell: ({ row }) => {
          const isExpanded = String(expandedRowId) === String(row.original.id);
          const collapseIcon = isExpanded ? (
            <CloseOutlined style={{ color: theme.palette.error.main }} />
          ) : (
            <EyeOutlined />
          );
          return (
            <Stack direction="row">
              <Tooltip title={isExpanded ? "close details" : "view details"}>
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedRowId(
                      isExpanded ? null : String(row.original.id)
                    );
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  {collapseIcon}
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(row.original);
                    setCustomerModal(true);
                  }}
                  sx={{ "&:hover": { backgroundColor: "transparent" } }}
                >
                  <EditOutlined />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                    setSelected(row.original);
                  }}
                >
                  <DeleteOutlined />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        },
      },
    ],
    [theme, expandedRowId]
  );

  const handleDelete = async () => {
    if (!selected?.id) return;
    setIsDeleting(true);
    try {
      await USERAXIOS.delete(`/resume/languages/${selected?.id}`);
      setResult((prevResults: any[]) =>
        prevResults.filter((item) => item.id !== selected?.id)
      );
      openSnackbar({
        open: true,
        message: "Resume Course deleted successfully",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        close: false,
        alert: {
          color: "success",
        },
      } as SnackbarProps);
      if (handleClose) handleClose();
    } catch (error: any) {
      openSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Failed to delete resume course",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setIsDeleting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await USERAXIOS.get(`/resume/languages`);
      const { data, meta } = response.data;
      setResult(data);
    } catch (error) {
      openSnackbar({
        open: true,
        message: "Error fetching articles",
        anchorOrigin: { vertical: "top", horizontal: "right" },
        variant: "alert",
        alert: {
          color: "error",
        },
      } as SnackbarProps);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <MainCard
          title="Resume Languages"
          headerActions={
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setCustomerModal(true);
                setSelected(null);
              }}
            >
              Add Language
            </Button>
          }
        >
          <ReusableSimpleTable
            data={result}
            columns={columns}
            renderDetailPanel={({ row }: any) => (
              <ExpandingResumeCourseDetail data={row.original} />
            )}
            expandedRowId={expandedRowId}
            setExpandedRowId={setExpandedRowId}
          />
        </MainCard>
      )}
      <ReusableDeleteDialog
        title={selected?.language_id.toString()}
        open={open}
        handleClose={handleClose}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />

      {customerModal && (
        <AddEditResumeLanguageForm
          setResult={setResult}
          item={selected}
          open={customerModal}
          setOpen={setCustomerModal}
          resumeId={resumeId}
        />
      )}
    </>
  );
};

export default ResumeLanguages;
